import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from 'recharts';

const MONTHS = {
  '01': 'Jan',
  '02': 'Fev',
  '03': 'Mar',
  '04': 'Abr',
  '05': 'Mai',
  '06': 'Jun',
  '07': 'Jul',
  '08': 'Ago',
  '09': 'Set',
  10: 'Out',
  11: 'Nov',
  12: 'Dez',
};

const formatMonthLabel = (label) => {
  if (!label) return '';

  const [year, month] = String(label).split('-');

  if (!year || !month) return label;

  return `${MONTHS[month] || month}/${year.slice(-2)}`;
};

const formatTooltipLabel = (label) => {
  if (!label) return '';

  const [year, month] = String(label).split('-');

  if (!year || !month) return label;

  return `${MONTHS[month] || month}/${year.slice(-2)}`;
};

const toReturnPercentage = (value) => {
  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue)) return null;

  if (parsedValue > 2) {
    return parsedValue - 100;
  }

  return parsedValue * 100;
};

const formatPercentage = (value, decimals = 0) => {
  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue)) return '-';

  return `${parsedValue.toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}%`;
};

const hasValidSeriesData = (series) => {
  if (!Array.isArray(series)) return false;

  return series.some((value) => toReturnPercentage(value) !== null);
};

const getMonthDistance = (startLabel, endLabel) => {
  const [startYear, startMonth] = String(startLabel).split('-').map(Number);
  const [endYear, endMonth] = String(endLabel).split('-').map(Number);

  if (!startYear || !startMonth || !endYear || !endMonth) return 0;

  return (endYear - startYear) * 12 + (endMonth - startMonth);
};

const getResponsiveTickInterval = (width, dataLength) => {
  if (!dataLength) return 6;

  if (!width) return 6;

  let minPixelsPerTick = 76;

  if (width < 420) {
    minPixelsPerTick = 115;
  } else if (width < 640) {
    minPixelsPerTick = 100;
  } else if (width < 900) {
    minPixelsPerTick = 88;
  }

  const maxTicks = Math.max(2, Math.floor(width / minPixelsPerTick));
  const interval = Math.ceil((dataLength - 1) / Math.max(1, maxTicks - 1));

  return Math.max(1, interval);
};

const buildVisibleTicks = (data, interval = 6) => {
  if (!data?.length) return [];

  const ticks = data.filter((_, index) => index % interval === 0).map((item) => item.label);

  const lastLabel = data[data.length - 1]?.label;

  if (!lastLabel) return ticks;

  const alreadyHasLastLabel = ticks.includes(lastLabel);

  if (alreadyHasLastLabel) return ticks;

  const previousVisibleLabel = ticks[ticks.length - 1];
  const distanceFromPrevious = getMonthDistance(previousVisibleLabel, lastLabel);

  if (distanceFromPrevious < interval) {
    return [...ticks.slice(0, -1), lastLabel];
  }

  return [...ticks, lastLabel];
};

const useElementWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;

    if (!element) return undefined;

    const updateWidth = () => {
      setWidth(element.getBoundingClientRect().width || 0);
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);

      return () => {
        window.removeEventListener('resize', updateWidth);
      };
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  return [ref, width];
};

const BacktestTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="rounded-lg border border-gray-100 bg-white px-3 py-2 shadow-md">
      <p className="mb-2 text-xs font-semibold text-blueDarker">{formatTooltipLabel(label)}</p>

      <div className="space-y-1">
        {payload.map((item) => (
          <div key={item.dataKey} className="flex items-center gap-2 text-xs">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />

            <span className="text-gray-500">{item.name}:</span>

            <span className="font-semibold text-blueDarker">{formatPercentage(item.value, 2)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const XAxisTick = ({ x, y, payload, visibleTicks, fontSize = 14 }) => {
  const value = payload?.value;

  if (!value) return null;

  const isFirstTick = value === visibleTicks[0];
  const isLastTick = value === visibleTicks[visibleTicks.length - 1];

  let textAnchor = 'middle';

  if (isFirstTick) {
    textAnchor = 'start';
  }

  if (isLastTick) {
    textAnchor = 'end';
  }

  return (
    <text x={x} y={y + 18} textAnchor={textAnchor} fill="#174060" fontSize={fontSize}>
      {formatMonthLabel(value)}
    </text>
  );
};

const YAxisTick = ({ x, y, payload }) => {
  return (
    <text x={0} y={y} dy={4} textAnchor="start" fill="#174060" fontSize={14} fontWeight={600}>
      {formatPercentage(payload.value)}
    </text>
  );
};

const BacktestChart = ({ backtest, title = 'Backtest', className = '' }) => {
  const [chartContainerRef, chartWidth] = useElementWidth();

  const hasCdiData = useMemo(() => {
    return hasValidSeriesData(backtest?.cdi);
  }, [backtest]);

  const hasIpcaData = useMemo(() => {
    return hasValidSeriesData(backtest?.ipca);
  }, [backtest]);

  const hasFundData = useMemo(() => {
    return hasValidSeriesData(backtest?.fund);
  }, [backtest]);

  const chartData = useMemo(() => {
    if (!backtest) return [];
    if (!Array.isArray(backtest.labels)) return [];
    if (!backtest.labels.length) return [];

    return backtest.labels.map((label, index) => ({
      label,
      displayLabel: formatMonthLabel(label),
      carteira: toReturnPercentage(backtest.carteira?.[index]),
      cdi: hasCdiData ? toReturnPercentage(backtest.cdi?.[index]) : null,
      ipca: hasIpcaData ? toReturnPercentage(backtest.ipca?.[index]) : null,
      fund: hasFundData ? toReturnPercentage(backtest.fund?.[index]) : null,
    }));
  }, [backtest, hasCdiData, hasIpcaData, hasFundData]);

  const tickInterval = useMemo(() => {
    return getResponsiveTickInterval(chartWidth, chartData.length);
  }, [chartWidth, chartData.length]);

  const visibleTicks = useMemo(() => {
    return buildVisibleTicks(chartData, tickInterval);
  }, [chartData, tickInterval]);

  const xAxisFontSize = chartWidth && chartWidth < 480 ? 12 : 14;

  const visibleTickSet = useMemo(() => new Set(visibleTicks), [visibleTicks]);

  const renderIpcaDot = ({ cx, cy, payload }) => {
    if (!payload?.label) return null;
    if (!visibleTickSet.has(payload.label)) return null;

    return <circle cx={cx} cy={cy} r={2} fill="#E45757" stroke="none" />;
  };

  const renderFundDot = ({ cx, cy, payload }) => {
    if (!payload?.label) return null;
    if (!visibleTickSet.has(payload.label)) return null;

    return <circle cx={cx} cy={cy} r={2} fill="#7C5CC4" stroke="none" />;
  };

  if (!chartData.length) {
    return (
      <div className={`w-full rounded-custom20 bg-white ${className}`}>
        <h3 className="text-lg font-semibold text-blueDarker">{title}</h3>

        <div
          className="mt-6 flex items-center justify-center rounded-xl border border-dashed border-gray-200"
          style={{
            height: 280,
            minHeight: 280,
          }}
        >
          <span className="text-sm text-gray-400">Sem dados de backtest disponíveis</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full rounded-custom20 bg-white ${className}`}>
      <h3 className="mb-2 text-lg font-semibold text-blueDarker">{title}</h3>

      <div
        ref={chartContainerRef}
        className="w-full"
        style={{
          width: '100%',
          height: 330,
          minHeight: 330,
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 10, right: 0, left: 0, bottom: 12 }}>
            <defs>
              <linearGradient id="backtestCarteiraGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#38C2C8" stopOpacity={0.28} />
                <stop offset="95%" stopColor="#38C2C8" stopOpacity={0.03} />
              </linearGradient>
            </defs>

            <CartesianGrid vertical={false} stroke="#E5E7EB" strokeDasharray="0" />

            <XAxis
              dataKey="label"
              ticks={visibleTicks}
              tickLine={false}
              axisLine={false}
              interval={0}
              height={44}
              tickMargin={8}
              tick={(props) => <XAxisTick {...props} visibleTicks={visibleTicks} fontSize={xAxisFontSize} />}
            />

            <YAxis
              tickFormatter={(value) => formatPercentage(value)}
              tickLine={false}
              axisLine={false}
              width={52}
              tick={<YAxisTick />}
            />

            <Tooltip content={<BacktestTooltip />} />

            <Legend
              verticalAlign="top"
              align="right"
              iconType="plainline"
              iconSize={18}
              wrapperStyle={{
                top: -36,
                right: 0,
                fontSize: 16,
                color: '#174060',
              }}
            />

            <Area
              type="monotone"
              dataKey="carteira"
              name="Carteira"
              stroke="#38C2C8"
              strokeWidth={3}
              fill="url(#backtestCarteiraGradient)"
              fillOpacity={1}
              dot={false}
              activeDot={{
                r: 4,
                strokeWidth: 2,
                stroke: '#38C2C8',
                fill: '#FFFFFF',
              }}
              connectNulls
            />

            {hasCdiData && (
              <Line
                type="monotone"
                dataKey="cdi"
                name="CDI"
                stroke="#AFC6D6"
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={false}
                activeDot={{
                  r: 4,
                  strokeWidth: 2,
                  stroke: '#AFC6D6',
                  fill: '#ffffff',
                }}
                connectNulls
              />
            )}

            {hasIpcaData && (
              <Line
                type="monotone"
                dataKey="ipca"
                name="IPCA"
                stroke="#E45757"
                strokeWidth={1.5}
                dot={renderIpcaDot}
                activeDot={{
                  r: 4,
                  strokeWidth: 2,
                  stroke: '#E45757',
                  fill: '#FFFFFF',
                }}
                connectNulls
              />
            )}

            {hasFundData && (
              <Line
                type="monotone"
                dataKey="fund"
                name="Benchmark"
                stroke="#7C5CC4"
                strokeWidth={1.8}
                dot={renderFundDot}
                activeDot={{
                  r: 4,
                  strokeWidth: 2,
                  stroke: '#7C5CC4',
                  fill: '#FFFFFF',
                }}
                connectNulls
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BacktestChart;
